import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { API } from '@/App';
import {
  readPlayerState,
  writePlayerState,
  markPlayerClosed,
} from '@/utils/playerState';
/**
 * Global player state.
 *
 * Why this exists:
 *  - Prior to this provider, AudioPlayer was mounted *per-page*. Navigating
 *    Library → Crates remounted the <audio> element, killing playback.
 *  - iOS Safari pauses background audio when the page changes its <audio>
 *    element identity — so any per-page mount also caused the player to
 *    stop when the screen locked for long enough that React re-rendered the
 *    page on resume.
 *
 * What we hold here:
 *  - `playingTrack`  — currently active track (with full metadata)
 *  - `queue`         — array used for prev/next navigation
 *  - `isPlaying`     — visual state for the rest of the UI
 *
 * Persistence:
 *  - On every track change we save `{ trackId, queueIds, closed: false }`.
 *  - On mount we read the saved trackId, fetch its full metadata via
 *    `/api/tracks/{id}` and (optionally) the queue tracks too, then set
 *    them as the active session — so the player UI restores after iOS
 *    kills the tab during a long phone-lock.
 *  - `closePlayer()` writes `closed: true` so we don't auto-restore once
 *    the user dismissed the player intentionally.
 */
const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
  const [playingTrack, setPlayingTrack] = useState(null);
  const [queue, setQueue] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const restoredRef = useRef(false);
  // Reference to the <audio> element rendered by AudioPlayer. Pages tap
  // this from their click handlers to invoke audio.play() *synchronously*
  // in the user-gesture context — iOS Safari only grants background-audio
  // and lock-screen-controls rights when play() is called inline with a
  // gesture, not deferred via setTimeout / promise chains.
  const audioElRef = useRef(null);
  // Ref-mirror of playingTrack so `playTrack()`'s synchronous same-track
  // short-circuit can read the latest track without depending on stale
  // closure state.
  const playingTrackRef = useRef(null);
  useEffect(() => {
    playingTrackRef.current = playingTrack;
  }, [playingTrack]);

  const registerAudioElement = useCallback((el) => {
    audioElRef.current = el;
  }, []);

  // ── Next-track URL prefetch ─────────────────────────────────────────────
  // iOS Safari revokes the user-gesture token as soon as a Promise resolves
  // outside the same microtask. This means the OLD auto-advance flow
  //   fetch(nextTrack.audio_url) → set src → play()
  // gets play() rejected on iOS because the fetch broke the gesture chain.
  //
  // Fix: while the CURRENT track is playing, we prefetch the next queue
  // item's presigned R2 URL and stash it here. Then `handleTrackChange`
  // (invoked synchronously from the audio element's `ended` event) sets
  // src + calls play() in the SAME task as the ended handler — which iOS
  // treats as a continuation of the previous playback, no new gesture
  // required. Spinner-wheel-forever bug goes away.
  const prefetchedNextRef = useRef({ trackId: null, url: null, expires: 0 });

  const prefetchNextAudioUrl = useCallback(async (nextTrackId) => {
    if (!nextTrackId) return;
    // Skip if we already have a fresh URL for this track (10 min TTL —
    // matches R2 presigned URL default).
    const cached = prefetchedNextRef.current;
    if (cached.trackId === nextTrackId && cached.url && Date.now() < cached.expires) return;

    try {
      const token = (typeof localStorage !== 'undefined') ? localStorage.getItem('cratelab_token') : null;
      const res = await fetch(`${API}/tracks/${nextTrackId}/audio`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data && data.audio_url) {
        prefetchedNextRef.current = {
          trackId: nextTrackId,
          url: data.audio_url,
          expires: Date.now() + 9 * 60 * 1000,  // 9 min — under R2's 10-min presigned TTL
        };
      }
    } catch { /* silent — auto-advance falls back to fetch-on-demand */ }
  }, []);

  // Restore previous session on first mount.
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    const saved = readPlayerState();
    if (!saved || saved.closed || !saved.trackId) return;

    let cancelled = false;
    (async () => {
      try {
        const trackRes = await axios.get(`${API}/tracks/${saved.trackId}`);
        const track = trackRes?.data?.track;
        if (!track || cancelled) return;
        if (!track.has_audio && !track.storage_path) return;

        let resolvedQueue = [track];
        if (Array.isArray(saved.queueIds) && saved.queueIds.length > 0) {
          // Reuse remembered queue order. Fetch missing ones individually.
          const fetched = await Promise.all(
            saved.queueIds.map((id) =>
              axios
                .get(`${API}/tracks/${id}`)
                .then((r) => r?.data?.track || null)
                .catch(() => null)
            )
          );
          const valid = fetched.filter(Boolean);
          if (valid.length > 0) resolvedQueue = valid;
        }

        if (cancelled) return;
        setQueue(resolvedQueue);
        setPlayingTrack(track);

        // Set the audio element's src too so the restored player UI
        // can be played by tapping the play button. We do NOT auto-play
        // on restoration — iOS requires a user gesture and the
        // page-reload after a long phone-lock isn't one.
        const audio = audioElRef.current;
        if (audio) {
          try {
            const tk = (typeof localStorage !== 'undefined') ? localStorage.getItem('cratelab_token') : null;
            const tp = tk ? `?t=${encodeURIComponent(tk)}` : '';
            const r = await fetch(`${API}/tracks/${track.id}/audio${tp}`, {
              headers: tk ? { Authorization: `Bearer ${tk}` } : {},
            });
            const json = await r.json();
            if (json?.audio_url && audioElRef.current === audio && !cancelled) {
              audio.src = json.audio_url;
            }
          } catch { /* ignore */ }
        }
      } catch {
        /* network errors during restore are non-fatal */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const playTrack = useCallback((track, nextQueue = null) => {
    if (!track || !track.id) return;

    // ── Same-track pause/resume short-circuit ────────────────────────────
    // If the caller is asking to (re)play the track that's ALREADY loaded
    // in the <audio> element, treat this as a play/pause toggle instead of
    // rerunning the full fetch → src → play chain. That old chain had a
    // race: the async fetch of the audio URL resolves ~300–500ms *after*
    // the click, and calling `audio.play()` at that point silently un-does
    // the user's pause. This short-circuit closes the race entirely.
    const audio = audioElRef.current;
    const currentTrack = playingTrackRef.current;
    if (audio && currentTrack && currentTrack.id === track.id && audio.src) {
      if (audio.paused) {
        const p = audio.play();
        if (p && typeof p.catch === 'function') p.catch(() => { /* ignore */ });
        setIsPlaying(true);
      } else {
        audio.pause();
        setIsPlaying(false);
      }
      return;
    }

    // CRITICAL iOS user-gesture chain:
    //   1. Synchronously call audio.play() on the live <audio> element
    //      BEFORE any await — this claims the iOS user-gesture token so
    //      background-audio rights and lock-screen controls are granted.
    //   2. Fetch the JSON audio_url (no redirect, direct R2 URL).
    //   3. Set src to the R2 URL → audio.play().
    //
    // We deliberately do NOT pre-set a silent data: URI before play().
    // Some iOS Safari builds treat the subsequent src change as a "source
    // switch" mid-playback and refuse to play the new URL until a fresh
    // gesture. Instead we just call play() once on the bare element,
    // which iOS records as a gesture-bound play intent and honours when
    // the real src is set later in this same tick.
    if (audio) {
      try {
        const primePromise = audio.play();
        if (primePromise && typeof primePromise.catch === 'function') {
          primePromise.catch(() => { /* expected if no src yet */ });
        }
      } catch { /* ignore */ }
    }

    // Update React state so the player UI shows the new track.
    if (nextQueue && Array.isArray(nextQueue) && nextQueue.length > 0) {
      setQueue(nextQueue);
    } else {
      setQueue((prev) => (prev.length > 0 ? prev : [track]));
    }
    setPlayingTrack((prev) => {
      if (prev && prev.id === track.id) {
        return { ...track, togglePlay: Date.now() };
      }
      return track;
    });
    setIsPlaying(true);
    writePlayerState({
      trackId: track.id,
      queueIds:
        nextQueue && nextQueue.length > 0
          ? nextQueue.map((t) => t.id)
          : [track.id],
      closed: false,
    });

    // Now fetch the JSON URL and switch the audio element to it. We do
    // this OUTSIDE React state so the gesture chain isn't broken by
    // re-renders — pure imperative DOM work on the audio element.
    if (audio) {
      const token = (typeof localStorage !== 'undefined') ? localStorage.getItem('cratelab_token') : null;
      const tokenParam = token ? `?t=${encodeURIComponent(token)}` : '';
      fetch(`${API}/tracks/${track.id}/audio${tokenParam}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then((res) => res.json())
        .then((data) => {
          if (!data || !data.audio_url) return;
          if (audioElRef.current !== audio) return;

          // Setting src auto-triggers loading. We deliberately avoid
          // calling load() — in some iOS Safari builds load() after a
          // prior play() resets the element to paused/empty and races
          // the resource fetch, so the subsequent play() never actually
          // starts playback (audio.paused stays true even with
          // readyState === 4).
          audio.src = data.audio_url;

          const p = audio.play();
          if (p && typeof p.catch === 'function') {
            p.catch(() => {
              // If the play() promise rejected (gesture lost during
              // fetch on iOS), wait for canplay and retry. The earlier
              // prime-play() in Step 1 usually keeps the gesture alive
              // long enough for this retry to succeed.
              const onCanPlay = () => {
                audio.removeEventListener('canplay', onCanPlay);
                audio.play().catch((e2) => console.error('Retry play failed:', e2));
              };
              audio.addEventListener('canplay', onCanPlay, { once: true });
            });
          }
        })
        .catch((err) => console.error('Failed to fetch audio URL:', err));
    }
  }, []);

  const closePlayer = useCallback(() => {
    setPlayingTrack(null);
    setIsPlaying(false);
    setQueue([]);
    markPlayerClosed();
  }, []);

  // Imperative seek — used by clickable waveforms to jump the playhead
  // without re-triggering the whole playTrack gesture chain.
  const seek = useCallback((seconds) => {
    const audio = audioElRef.current;
    if (!audio || !Number.isFinite(seconds) || seconds < 0) return;
    try {
      audio.currentTime = seconds;
    } catch { /* ignore — Safari sometimes throws before metadata loads */ }
  }, []);

  const handleTrackChange = useCallback(
    (next) => {
      if (!next) return;
      setPlayingTrack(next);
      setIsPlaying(true);
      writePlayerState({
        trackId: next.id,
        queueIds: queue.map((t) => t.id),
        closed: false,
      });

      const audio = audioElRef.current;
      if (!audio) return;

      // ── Fast path (iOS-safe): use prefetched URL SYNCHRONOUSLY ────────
      // This is the critical path for auto-advance. If we prefetched the
      // next URL while the previous track was playing, we can set src +
      // play() in the same task as the `ended` event that triggered this
      // handler. iOS treats that as a valid continuation of the previous
      // gesture, no user tap required, no spinning wheel.
      const cached = prefetchedNextRef.current;
      if (cached.trackId === next.id && cached.url && Date.now() < cached.expires) {
        audio.src = cached.url;
        const p = audio.play();
        if (p && typeof p.catch === 'function') {
          p.catch((err) => console.warn('Sync auto-advance play failed:', err));
        }
        // Invalidate the cache so we don't reuse it after skipping around.
        prefetchedNextRef.current = { trackId: null, url: null, expires: 0 };
        return;
      }

      // ── Slow path (fallback): fetch, then set src + play ──────────────
      // Only hit on the very first track of a session or after a long
      // idle. iOS may reject play() here without a fresh gesture, but
      // that's acceptable — we retry via 'canplay' below and the user
      // just re-taps if the retry didn't stick.
      const token = (typeof localStorage !== 'undefined') ? localStorage.getItem('cratelab_token') : null;
      fetch(`${API}/tracks/${next.id}/audio`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
        .then((res) => res.json())
        .then((data) => {
          if (!data || !data.audio_url) return;
          if (audioElRef.current !== audio) return;
          audio.src = data.audio_url;
          const p = audio.play();
          if (p && typeof p.catch === 'function') {
            p.catch(() => {
              const onCanPlay = () => {
                audio.removeEventListener('canplay', onCanPlay);
                audio.play().catch((e2) => console.error('Retry play failed:', e2));
              };
              audio.addEventListener('canplay', onCanPlay, { once: true });
            });
          }
        })
        .catch((err) => console.error('Failed to fetch next track audio URL:', err));
    },
    [queue]
  );

  const value = useMemo(
    () => ({
      playingTrack,
      queue,
      isPlaying,
      setIsPlaying,
      playTrack,
      handleTrackChange,
      closePlayer,
      seek,
      setQueue,
      registerAudioElement,
      audioElRef,
      prefetchNextAudioUrl,
    }),
    [playingTrack, queue, isPlaying, playTrack, handleTrackChange, closePlayer, seek, registerAudioElement, prefetchNextAudioUrl]
  );

  return <PlayerContext.Provider value={value}>{children}</PlayerContext.Provider>;
}

export function usePlayer() {
  const ctx = useContext(PlayerContext);
  if (!ctx) {
    // Allow components to safely call usePlayer() outside the provider —
    // useful for the few pages that may render before the provider mounts.
    return {
      playingTrack: null,
      queue: [],
      isPlaying: false,
      setIsPlaying: () => {},
      playTrack: () => {},
      handleTrackChange: () => {},
      closePlayer: () => {},
      seek: () => {},
      setQueue: () => {},
      registerAudioElement: () => {},
      audioElRef: { current: null },
      prefetchNextAudioUrl: () => {},
    };
  }
  return ctx;
}
